import React, { useState } from "react";
import { FileText, Download } from "lucide-react";
import Modal from "../../../components/modal";
import { Button } from "../../../components/button";

type TeamStatus = "accepted" | "registered" | "all";

interface StatusOption {
  value: TeamStatus;
  label: string;
  description: string;
}

interface Competition {
  id: number;
  name: string;
  edition?: number;
  accepted_teams: number;
  registered_teams: number;
  total_teams: number;
}

interface PdfExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (status: TeamStatus) => Promise<void>;
}

export const PdfExportModal: React.FC<PdfExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<TeamStatus>("accepted");
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const statusOptions: StatusOption[] = [
    {
      value: "accepted",
      label: "Equipos pagos/confirmados",
      description: "Solo equipos que han completado el pago",
    },
    {
      value: "registered",
      label: "Equipos registrados",
      description: "Todos los equipos registrados (sin pagar)",
    },
    {
      value: "all",
      label: "Todos los equipos",
      description: "Equipos pagos y registrados",
    },
  ];

  const handleExport = async (): Promise<void> => {
    setIsExporting(true);
    try {
      await onExport(selectedStatus);
      //onClose();
    } catch (error) {
      throw new Error("Error al generar el PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const handleStatusChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSelectedStatus(event.target.value as TeamStatus);
  };

  const modalTitle = (
    <div className="flex items-center gap-3">
      <div className="p-2 bg-orange-100 rounded-lg">
        <FileText className="h-5 w-5 text-orange-600" />
      </div>
      <div>
        <span className="text-lg font-semibold text-gray-900">
          Exportar PDF de Equipos
        </span>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} size="md">
      {/* Content */}
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Selecciona el tipo de equipos a exportar:
          </label>
          <div className="space-y-3">
            {statusOptions.map((option: StatusOption) => (
              <label
                key={option.value}
                className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedStatus === option.value
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="status"
                  value={option.value}
                  checked={selectedStatus === option.value}
                  onChange={handleStatusChange}
                  className="mt-0.5 mr-3 text-orange-600 focus:ring-orange-500"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg justify-end">
        <Button
          variant="cancel"
          onClick={onClose}
          disabled={isExporting}
          type="button"
          text="Cancelar"
        />
        <Button
          variant="create"
          onClick={handleExport}
          disabled={isExporting}
          type="button"
          text={isExporting ? "Generando..." : `Exportar PDF`}
          icon={<Download className="w-5 h-5 mr-2" />}
        />
      </div>
    </Modal>
  );
};

export type { TeamStatus, Competition, PdfExportModalProps };
